import { pushToDataLayer } from "../pushToDatalayer";
import { trackSnackbarViewcart } from "./cart";

/**
 * Track product related events for the overview and detail pages.
 *
 * @returns {void}
 */
function initProductTracking() {
  trackProductOverview();
  trackProductDetail();
}

/**
 * Track events on the product overview page. These include:
 * - Click on list/grid view
 * - Click on product (detail link or product title and active product group)
 * - Use of dropdown, incl. selected value
 * - Product added to cart
 *
 * @returns {void}
 */
function trackProductOverview() {
  const productsList = document.querySelector(".products-list");
  if (!productsList) return;

  productsList.addEventListener("click", (e) => {
    trackProductClicks(e);
    trackAddToCart(e);
  });

  /**
   * Track clicks on products to detail view.
   * It includes the product title and the active product group
   * if available.
   *
   * @param {Event} e The event object
   * @returns {void}
   */
  function trackProductClicks(e) {
    const target =
      e.target.closest(".products-list__detail-link") ||
      e.target.closest(".products-list__productname");
    if (!target) return;
    const product = target.closest(".products-list__item");
    const title = product.querySelector(
      ".products-list__productname"
    ).textContent;
    const productGroupName = getProductGroupName();
    const value = productGroupName
      ? `${title} | Selected Product Group: ${productGroupName}`
      : title;

    pushToDataLayer("product", {
      eventCategory: "product overview",
      eventAction: "click",
      eventLabel: "product click",
      eventValue: value,
    });
  }

  /**
   * Track adding products to the cart.
   * It includes the product title and the active product group
   * if available.
   *
   * @param {Event} e The event object
   * @returns {void}
   */
  function trackAddToCart(e) {
    const target = e.target.closest(".products-list__cart-button");
    if (!target) return;
    const product = target.closest(".products-list__item");
    const title = product.querySelector(
      ".products-list__productname"
    ).textContent;
    const productGroupName = getProductGroupName();
    const value = productGroupName
      ? `${title} | Selected Product Group: ${productGroupName}`
      : title;

    let label = "add to cart";
    let productOrPart = null;
    if (document.getElementById("type1")?.checked) {
      productOrPart = "product";
    }
    if (!productOrPart && document.getElementById("type2")?.checked) {
      productOrPart = "part";
    }
    if (productOrPart) {
      label = `add ${productOrPart} to cart`;
    }

    pushToDataLayer("product", {
      eventCategory: "product overview",
      eventAction: "click",
      eventLabel: label,
      eventValue: value,
    });

    trackSnackbarViewcart();
  }

  /**
   * Track view change from list to grid and vice versa.
   *
   * @returns {void}
   */
  function trackViewChange() {
    const viewChange = document.querySelector(".products-filter__layouts");
    if (!viewChange) return;
    viewChange.addEventListener("click", (e) => {
      const label = e.target.closest(".products-filter__label");
      if (!label) return;
      const view = label.getAttribute("for");
      const value = view == "layout1" ? "list" : "grid";
      pushToDataLayer("product", {
        eventCategory: "product overview",
        eventAction: "click",
        eventLabel: "view change",
        eventValue: value,
      });
    });
  }

  /**
   * Track changes in the dropdown of the product group.
   *
   * @returns {void}
   */
  function trackDropdown() {
    const dropdown = document.querySelector(".products-filter__options select");
    if (!dropdown) return;

    dropdown.addEventListener("change", (e) => {
      pushToDataLayer("product", {
        eventCategory: "product overview",
        eventAction: "change",
        eventLabel: "dropdown",
        eventValue: getProductGroupName(),
      });
    });
  }

  /**
   * Get the product group name from the dropdown.
   *
   * @returns {string} The product group name
   */
  function getProductGroupName() {
    const dropdown = document.querySelector(".products-filter__options select");
    if (!dropdown) return;
    const selectedOption = dropdown.options[dropdown.selectedIndex];
    return selectedOption.text;
  }

  trackViewChange();
  trackDropdown();
}

/**
 * Track events on the product detail page. These include:
 * - Click on product image
 * - Product added to cart (incl. quantity for parts)
 * - Part added to cart (incl. quantity)
 *
 * @returns {void}
 */
function trackProductDetail() {
  /**
   * Track clicks on the product image.
   *
   * @returns {void}
   */
  function trackProductImageClick() {
    const productImage = document.querySelector(".product-detail__image");
    if (!productImage) return;

    productImage.addEventListener("click", () => {
      pushToDataLayer("product", {
        eventCategory: "product detail",
        eventAction: "click",
        eventLabel: "product image",
        eventValue: document.querySelector(".product-detail__name").textContent,
      });
    });
  }

  /**
   * Track adding products to the cart.
   *
   * @returns {void}
   */
  function trackAddProductToCart() {
    const addToCart = document.querySelector(".product-detail__cart-btn");
    if (!addToCart) return;

    addToCart.addEventListener("click", () => {
      pushToDataLayer("product", {
        eventCategory: "product detail",
        eventAction: "click",
        eventLabel: "add product to cart",
        eventValue: document.querySelector(".product-detail__name").textContent,
      });
    });
  }

  /**
   * Track adding parts to the cart.
   *
   * @returns {void}
   */
  function trackAddPartToCart() {
    const partList = document.querySelector(".cart__table tbody");
    if (!partList) return;
    partList.addEventListener("click", (e) => {
      const target = e.target.closest(".button.button--secondary");
      if (!target) return;
      const part = target.closest(".cart__table tr");
      const title = part.querySelector(
        "td[data-title='Item Description']"
      ).textContent;
      const quantity = part.querySelector(".cart__quantity").value;

      pushToDataLayer("product", {
        eventCategory: "product detail",
        eventAction: "click",
        eventLabel: "add part to cart",
        eventValue: `${title} | Quantity: ${quantity}`,
      });
    });
  }

  trackProductImageClick();
  trackAddProductToCart();
  trackAddPartToCart();
}

export default initProductTracking;
